/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';
import { v4 as uuidv4 } from 'uuid';
import { IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Menu, MenuItem } from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

import { CameraModal } from '@components/CameraModal';
import { PhotoViewModal } from '@components/PhotoViewModal';
import { RootState } from '@state-mgmt/store';
import { resizeImage } from '@utils/resizeImage';
import { isMobile } from '@utils/isMobile';
import { styles } from './styles';
import { useUploadOptionsModal } from '@hooks/modals/useUploadOptionsModal';
import { UPLOAD_OPTION } from '@components/UploadOptionsModal/UploadOptionsModal';
import { detectIOS } from '@utils/detectIOS';
import DocumentBox from './DocumentBox';
import { useDeleteUploadConfirmationModal } from '@hooks/modals/useDeleteUploadConfirmationModal';

interface UploadValue {
  valueId?: string | null; //api id for saved image
  url?: string | null; //api url for saved image
  remove?: boolean | null; // local tracking to delete saved images from api

  id?: string | null; //local id for new
  image?: string | null; // local image for new

  description?: string | null | undefined;

  latitude?: number | null;
  longitude?: number | null;
}

export interface Props {
  value: UploadValue[] | undefined;
  onChange: (values: any[] | undefined, skipLocation: boolean) => void;
}

const UploadCharacteristicType = ({ value, onChange }: Props) => {
  const { t } = useTranslation();

  const { showBoundary } = useErrorBoundary();
  const { currentPosition } = useSelector((state: RootState) => state.app);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const { displayModal: displayUploadOptionsModal } = useUploadOptionsModal();

  const [controlledValue, setControlledValue] = useState<UploadValue[] | undefined>(value || []);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [photo, setPhoto] = useState<any>();
  const [photoPermissionsAlertOpen, setPhotoPermissionsAlertOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const { displayModal: displayConfirmDeleteUploadModal } = useDeleteUploadConfirmationModal();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onConfirmPhoto = (image: string, description: string) => {
    try {
      const prevValues = controlledValue ? [...controlledValue] : [];
      const newValues = [...prevValues, { image, id: uuidv4(), ...currentPosition, description }];
      setControlledValue(newValues);
      onChange(newValues, false);
      setCameraModalOpen(false);
    } catch (error) {
      showBoundary(error);
    }
  };

  const onUpdatePhotoDescription = (id: string, valueId: string, description: string) => {
    const prevValues = controlledValue ? [...controlledValue] : [];

    if (valueId) {
      const updatedItems = prevValues.map(item =>
        item.valueId === valueId
          ? {
              ...item,
              description
            }
          : item
      );
      setControlledValue(updatedItems);
      onChange(updatedItems, true);
      setCameraModalOpen(false);
    } else if (id) {
      const updatedItems = prevValues.map(item =>
        item.id === id
          ? {
              ...item,
              description
            }
          : item
      );
      setControlledValue(updatedItems);
      onChange(updatedItems, true);
      setCameraModalOpen(false);
    }
  };

  const addSrc = async (e: any) => {
    try {
      const images: any = [];
      for (const file of e.target.files) {
        if (file) {
          const image = await resizeImage(file, '1080p');
          images.push({ image, id: uuidv4(), ...currentPosition });
        }
      }
      const prevValues = controlledValue ? [...controlledValue] : [];
      const newValues = [...prevValues, ...images];
      setControlledValue(newValues);
      onChange(newValues, false);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleAddPhoto = async () => {
    try {
      if (isMobile()) {
        if (detectIOS()) {
          galleryInputRef.current?.click();
        } else {
          displayUploadOptionsModal(option => {
            if (option === UPLOAD_OPTION.CAMERA) {
              cameraInputRef.current?.click();
            } else {
              galleryInputRef.current?.click();
            }
          });
        }
      } else {
        try {
          await navigator.mediaDevices.getUserMedia({
            video: true
          });
          setCameraModalOpen(true);
        } catch (error) {
          setPhotoPermissionsAlertOpen(true);
          console.error(error);
        }
      }
    } catch (error) {
      showBoundary(error);
    }
  };

  const onDelete = (id: string | undefined, valueId: any) => {
    if (valueId) {
      const uploadToDelete = { ...controlledValue?.find((cv: any) => cv.valueId && cv.valueId === valueId) };
      uploadToDelete['remove'] = true;
      const result = controlledValue?.filter(cv => (cv.valueId && cv.valueId !== valueId) || cv.image);
      const newValue = [...(result || []), uploadToDelete];
      setControlledValue(newValue);
      onChange(newValue, true);
    } else {
      const newValue = controlledValue?.filter(v => v.id !== id);
      setControlledValue(newValue);
      onChange(newValue, true);
    }
  };

  const isDisplayableImage = (url: string): boolean => {
    try {
      const pathname = new URL(url).pathname;

      return /\.(jpe?g|png|gif|webp|svg|avif)$/i.test(pathname);
    } catch {
      return false;
    }
  };

  return (
    <>
      <div css={styles.container}>
        {controlledValue?.map(({ image, id, url, valueId, remove, description }) => {
          const isImage = image || isDisplayableImage(url || '');

          if (isImage) {
            return (
              <div key={id || valueId} css={styles.thumbnailContainer}>
                <div css={styles.description}>{description || <>&nbsp;</>}</div>

                <div
                  style={{ backgroundImage: `url(${image || url})` }}
                  css={[styles.thumbnail, remove && styles.hidden]}
                  onClick={() => {
                    setPhoto({ id, valueId, src: image || url, description });
                  }}
                />
              </div>
            );
          } else if (url && !remove) {
            return <DocumentBox key={id || valueId} url={url} onDelete={() => displayConfirmDeleteUploadModal(() => onDelete(undefined, valueId))} />;
          }
        })}

        <div css={styles.addPhotoBtn}>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" css={styles.inputget} onChange={addSrc} />
          <input ref={galleryInputRef} type="file" accept="image/*" id="gallery" multiple css={styles.inputget} onChange={addSrc} />
          <IconButton css={styles.addPhotoBtnIconContainer} onClick={handleAddPhoto}>
            <AddAPhotoIcon css={styles.addPhotoBtnIcon} />
          </IconButton>
        </div>
      </div>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
          }}
        >
          {t('general.delete')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
          }}
        >
          {t('caracteristics.re-center-location-button')}
        </MenuItem>
      </Menu>

      <PhotoViewModal
        open={!!photo}
        photo={photo}
        onClose={() => setPhoto(undefined)}
        onPhotoDeleted={(id, valueId) => displayConfirmDeleteUploadModal(() => onDelete(id, valueId))}
        onUpdatePhotoDescription={onUpdatePhotoDescription}
      />

      <CameraModal open={cameraModalOpen} onClose={() => setCameraModalOpen(false)} onConfirm={onConfirmPhoto} />

      <Dialog open={photoPermissionsAlertOpen} onClose={() => setPhotoPermissionsAlertOpen(true)}>
        <DialogTitle> {t('camera-permissions.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText css={styles.dialogText}>{t('camera-permissions.text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoPermissionsAlertOpen(false)} variant="contained" autoFocus>
            {t('general.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadCharacteristicType;
